// The address half of a search list, as `SearchGroup`s.
//
// `/api/search/address` answers with one flat array that mixes two tiers: the
// district's own situs parcels and the geocoder's street-level suggestions.
// The tier order is the point — a situs hit is a real parcel the district can
// act on, a geocoded suggestion is only a place — so the situs tier is always
// printed above the geocoded one, and an empty tier is omitted rather than
// printed as an empty heading. Both the Map Review finder and Reveal's Sites
// panel read their address lists through here, and the function is pure so the
// ordering, the row text and the payload identity can be pinned by tests.
import type { AddressSearchResult } from "@/type/api";
import type { SearchGroup, SearchResult } from "./types";

/** A result's readable title: the situs address, else the geocoder's own
 * descriptive detail. */
function titleOf(result: AddressSearchResult): string {
	if (result.kind === "situs") {
		return result.address || result.locality || "Address";
	}
	return result.detail || result.locality || "Location";
}

/** A result's secondary line: where it is, and what the tier adds — a situs
 * parcel's assessor pool count, a geocoded hit's own type. */
function detailOf(result: AddressSearchResult): string {
	const bits: string[] = [];
	if (result.locality) bits.push(result.locality);
	if (result.kind === "situs") {
		if (result.poolCount != null) bits.push(`${result.poolCount} pools`);
	} else if (result.type) {
		bits.push(result.type);
	}
	return bits.join(" · ");
}

/** One tier's rows, in the order the server returned them. The key falls back
 * to the row's position within the tier only when the result carries no id of
 * its own, which is the one case where nothing stabler exists. */
function rowsOf(
	results: AddressSearchResult[],
	kind: "situs" | "geocode",
): SearchResult<AddressSearchResult>[] {
	return results.map((result, index) => ({
		key:
			kind === "situs"
				? `situs-${result.id ?? index}`
				: `geocode-${result.gid ?? index}`,
		title: titleOf(result),
		detail: detailOf(result) || undefined,
		icon: kind === "situs" ? "bi bi-house" : "bi bi-geo-alt",
		// A parcel with no assessor pools has nothing to count, so the badge is
		// left off rather than printed as a zero beside the address.
		badge:
			kind === "situs" && result.poolCount
				? String(result.poolCount)
				: undefined,
		payload: result,
	}));
}

export function addressGroups(
	results: AddressSearchResult[],
): SearchGroup<AddressSearchResult>[] {
	const situs = rowsOf(
		results.filter((result) => result.kind === "situs"),
		"situs",
	);
	const geocode = rowsOf(
		results.filter((result) => result.kind === "geocode"),
		"geocode",
	);
	const groups: SearchGroup<AddressSearchResult>[] = [];
	if (situs.length > 0) {
		groups.push({
			key: "situs",
			label: "Sites & parcels",
			icon: "bi bi-house",
			results: situs,
		});
	}
	if (geocode.length > 0) {
		groups.push({
			key: "geocode",
			label: "Geocoded",
			icon: "bi bi-geo-alt",
			results: geocode,
		});
	}
	return groups;
}
